/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Who leaves when an agency gives seats up, decided on the screen.
 *
 * The same rules the server keeps, because the screen has to draw them
 * before anything is sent: a candidate that cannot be marked is drawn
 * disabled rather than swallowing the click, and the confirm button waits
 * for every seat being given up to have somebody named.
 *
 * Mirrors `housien/services/billing/seat_reduction.py`. The server is the
 * authority — it re-validates and re-prunes everything this decides — so the
 * two can disagree without anybody losing a seat by accident.
 */

import type { TeamSeatBlock } from '@/services/requests/housien-services/AgenciesService.team';

/** What one chosen thing can be. */
export type LeavingKind = 'person' | 'vacant' | 'invitation';

/** One seat chosen to empty when the reduction lands. */
export interface LeavingEntry {
  plan_id: string;
  kind: LeavingKind;
  /** Absent on a seat bought and never filled: nobody minted an id for it. */
  seat_id?: string | null;
  /** Which unfilled seat of the block this is, when there is no id. */
  slot?: number | null;
  invitation_id?: string | null;
  email?: string | null;
  /** Set once a chosen invitation has actually been revoked. */
  cancelled_at?: string | null;
}

/** One line of the subscription, as it stands and as it will. */
export interface SubscriptionLine {
  plan_id: string;
  quantity: number;
}

/**
 * How many seats of each plan a scheduled change lets go.
 *
 * Only reductions count: a plan that grew in the same change is paid for now
 * and has nobody to choose.
 */
export const seatsGivenUp = (
  items: SubscriptionLine[],
  scheduled: { items: SubscriptionLine[] } | null | undefined,
): Record<string, number> => {
  if (!scheduled) return {};
  const later = new Map(scheduled.items.map((line) => [line.plan_id, line.quantity]));
  const given: Record<string, number> = {};
  (items || []).forEach((line) => {
    const difference = line.quantity - (later.get(line.plan_id) ?? 0);
    if (difference > 0) given[line.plan_id] = difference;
  });
  return given;
};

/**
 * What makes two choices the same choice.
 *
 * `slot` is in here because a seat bought and never filled has no id —
 * nobody ever sat in it to mint one. A block giving up two of those would
 * otherwise be choosing the same nothing twice.
 */
const keyOf = (entry: LeavingEntry): string => [
  entry.kind, entry.seat_id ?? '', entry.invitation_id ?? '',
  entry.slot ?? '', (entry.email ?? '').trim().toLowerCase(),
].join('|');

/** Whether this choice can no longer be taken back. */
export const isLocked = (entry: LeavingEntry): boolean => Boolean(entry.cancelled_at);

/**
 * Find chosen.
 */
const findChosen = (leaving: LeavingEntry[], entry: LeavingEntry) => (
  leaving.find((chosen) => keyOf(chosen) === keyOf(entry))
);

/**
 * Whether this candidate may still be marked.
 *
 * Asked before the row is drawn, so a block with no room left shows its
 * remaining candidates disabled instead of swallowing a click. Something
 * already marked answers true — clicking it is unmarking it — unless it is
 * locked, in which case it is a fact rather than a checkbox.
 */
export const canChoose = (
  leaving: LeavingEntry[], entry: LeavingEntry, capacity: number,
): boolean => {
  const already = findChosen(leaving, entry);
  if (already) return !isLocked(already);
  if (capacity <= 0) return false;
  return leaving.filter((chosen) => chosen.plan_id === entry.plan_id).length < capacity;
};

/**
 * Add one choice, refusing when the block is already full.
 *
 * Changing the choice is two acts, not one: whoever clicks means "this person
 * too", and a screen that answered by quietly releasing somebody else would
 * be deciding who leaves an agency on the operator's behalf.
 */
export const choose = (
  leaving: LeavingEntry[], entry: LeavingEntry, capacity: number,
): LeavingEntry[] => {
  if (findChosen(leaving, entry)) return [...leaving];
  if (!canChoose(leaving, entry, capacity)) return [...leaving];
  return [...leaving, entry];
};

/** Take one choice back, unless it has already happened. */
export const unchoose = (
  leaving: LeavingEntry[], entry: LeavingEntry,
): LeavingEntry[] => leaving.filter(
  (chosen) => keyOf(chosen) !== keyOf(entry) || isLocked(chosen),
);

/** Whether every seat being given up now has one named. */
export const isComplete = (
  leaving: LeavingEntry[], givenUp: Record<string, number>,
): boolean => Object.entries(givenUp).every(
  ([planId, count]) => leaving.filter((entry) => entry.plan_id === planId).length === count,
);

/** How many of a plan's seats still need somebody named. */
export const stillToChoose = (
  leaving: LeavingEntry[], planId: string, givenUp: Record<string, number>,
): number => Math.max(
  0, (givenUp[planId] ?? 0) - leaving.filter((entry) => entry.plan_id === planId).length,
);

/** One candidate the screen offers, with everything it needs to draw it. */
export interface Candidate extends LeavingEntry {
  label: string;
  hint?: string;
}

/**
 * The candidates of one plan, empty seats first.
 *
 * They come first and pre-marked because letting one go affects nobody, and
 * an operator who has to give up two seats should be offered the free one
 * before being asked to choose a colleague.
 */
export const candidatesFor = (
  planId: string,
  block: TeamSeatBlock | undefined,
  people: Array<{ seat_id: string | null; email: string; plan_id: string | null; role: string | null }>,
  pending: Array<{ id: string | null; email: string; plan_id: string | null }>,
): Candidate[] => {
  const free = Math.max(0, block?.free ?? 0);
  const vacant: Candidate[] = Array.from({ length: free }, (_, slot) => ({
    plan_id: planId, kind: 'vacant', seat_id: null, slot, label: '',
  }));
  const invitations: Candidate[] = pending
    .filter((invite) => invite.plan_id === planId)
    .map((invite) => ({
      plan_id: planId, kind: 'invitation', invitation_id: invite.id,
      email: invite.email, label: invite.email,
    }));
  // The owner is never a candidate: an agency cannot give up the seat of the
  // person who owns it.
  const seated: Candidate[] = people
    .filter((person) => person.plan_id === planId && person.role !== 'owner')
    .map((person) => ({
      plan_id: planId, kind: 'person', seat_id: person.seat_id,
      email: person.email, label: person.email,
    }));
  return [...vacant, ...invitations, ...seated];
};

/**
 * The choice a block opens with: its free seats, up to what it is giving up.
 *
 * Pre-marking them is not a shortcut. It is the only choice that costs
 * nobody anything, so it is the one the screen should have made already.
 */
export const preChosen = (
  planId: string, block: TeamSeatBlock | undefined, capacity: number,
): LeavingEntry[] => Array.from(
  { length: Math.min(Math.max(0, block?.free ?? 0), Math.max(0, capacity)) },
  (_, slot) => ({ plan_id: planId, kind: 'vacant' as const, seat_id: null, slot }),
);

/**
 * The chosen seats, in the shape the server's schema accepts.
 *
 * Keys whose value is null are dropped rather than sent. The request body is
 * validated with JSON Schema draft 7, which has no notion of OpenAPI's
 * `nullable: true` -- a field declared `type: string` refuses null outright,
 * whatever the document says beside it. The schema's own wording is
 * "absent on a seat bought and never filled", and absent is not null.
 *
 * @param leaving - The choice as the screen holds it.
 * @returns The same lines, without their empty keys.
 */
export const leavingPayload = (
  leaving: LeavingEntry[],
): Array<Record<string, unknown>> => leaving.map((entry) => Object.fromEntries(
  Object.entries(entry).filter(([, value]) => value !== null && value !== undefined),
));

/**
 * The scheduled lines, spoken in the identifiers the contract uses.
 *
 * Payien answers in its own canonical plan id -- the catalogue's UUID -- while
 * the host asks in slugs, and `_priced_line` echoes the entry it matched
 * rather than the key it was given. So a schedule for "5 Esmeralda" came back
 * under a plan id the contracted lines have never heard of: nothing matched,
 * the whole block looked given up, and "Trocar quem sai" asked which six of
 * six seats were leaving a reduction of one.
 *
 * Matching is positional through the alias list rather than by guessing which
 * spelling is canonical: either side may be the slug or the id, and the host
 * is the only one holding both.
 *
 * @param scheduled - The scheduled change as the server reports it.
 * @param items - The contracted lines, in the host's own identifiers.
 * @param aliasesOf - Every identifier a contracted plan answers to.
 * @returns The same schedule, keyed like the contract.
 */
export const alignToContracted = (
  scheduled: { items: SubscriptionLine[]; effective_at?: string | null } | null | undefined,
  items: SubscriptionLine[],
  aliasesOf: (planId: string) => string[],
): { items: SubscriptionLine[]; effective_at?: string | null } | null => {
  if (!scheduled) return null;
  const byAlias = new Map<string, string>();
  (items || []).forEach((line) => {
    aliasesOf(line.plan_id).forEach((alias) => {
      if (alias) byAlias.set(alias, line.plan_id);
    });
    byAlias.set(line.plan_id, line.plan_id);
  });
  return {
    ...scheduled,
    items: (scheduled.items || []).map((line) => ({
      ...line,
      plan_id: byAlias.get(line.plan_id) ?? line.plan_id,
    })),
  };
};
